import React, { ReactNode } from 'react';

import { Redirect, Route, RouteProps } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';

import { FunctionalRootState, isDisplayCertificates, LoginType, isPublicClubSiteAnonymousVisitor } from '@share/utils';
import { Routes } from '@share/constants';
import { IAccount, IUser } from '@share/common-types';

interface PrivateCertificateRouteProps extends RouteProps {
  children: ReactNode;
  isSignIn?: boolean;
}

interface IsAuthenticatedParams {
  isSignIn?: boolean;
  account: IAccount;
  user: IUser | null;
  loading: boolean;
  accessToken: string | null;
}

function computeIsAuthenticated({
  isSignIn,
  account,
  user,
  loading,
  accessToken,
}: IsAuthenticatedParams): boolean {
  if (isSignIn) {
    return !!account && account.type === LoginType.PrivateWithLogin;
  }

  const isPrivateWithLogin = account?.type === LoginType.PrivateWithLogin;
  const isPrivateWithToken = account?.type === LoginType.PrivateWithToken;
  const isPublic = account?.type === LoginType.Public;
  const isPublicClubSiteAnonymous = isPublicClubSiteAnonymousVisitor(account);
  return (
    isPublic ||
    isPublicClubSiteAnonymous ||
    !!user ||
    (isPrivateWithLogin && !isEmpty(accessToken) && account?.allowLoginTokenPrivateWithLogin) ||
    (isPrivateWithToken && loading)
  );
}

export function PrivateCertificateRoute({
  children,
  isSignIn,
  ...routeProps
}: PrivateCertificateRouteProps): React.ReactElement | null {
  const loginStore = useSelector((state: FunctionalRootState) => state.loginStore);
  const { account, user, loading, accessToken, widgetRedirect } = loginStore;

  if (!account) {
    return null;
  }

  if (loading) {
    return null;
  }

  const isAuthenticated = computeIsAuthenticated({
    isSignIn,
    account,
    user,
    loading,
    accessToken,
  });

  const isPrivateWithLogin = account?.type === LoginType.PrivateWithLogin;

  const widgetLogoutRedirect = account?.widgetLogoutRedirect;
  const disableSignInRedirect = account?.disableSignInRedirect;
  const isDisableSignIn = account?.isDisableSignIn;

  const pathname = isSignIn ? Routes.NotFound : `/${account.name}${Routes.Login}`;

  const widgetRedirectFinal = widgetRedirect || widgetLogoutRedirect;

  if (!isAuthenticated && isPrivateWithLogin) {
    if (widgetRedirectFinal && !isEmpty(widgetRedirectFinal)) {
      window.location.replace(widgetRedirectFinal);
      return null;
    }

    if (isDisableSignIn && disableSignInRedirect && !isEmpty(disableSignInRedirect)) {
      window.location.replace(disableSignInRedirect);
      return null;
    }
  }

  return (
    <Route
      {...routeProps}
      render={({ location }) =>
        isAuthenticated ? (
          isDisplayCertificates(account) ? (
            children
          ) : (
            <Redirect
              to={{
                pathname: Routes.CustomError,
                state: {
                  data: 'UNAUTHORIZED',
                },
              }}
            />
          )
        ) : (
          <Redirect
            to={{
              pathname,
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}
